import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Brain } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MLPanel({ project, reload, setOverlay, activeOverlayKey }) {
  const id = project.id;
  const ml = project.prospectivity_ml;
  const bbox = project.boundary_metrics?.bbox;
  const [pts, setPts] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoBarren, setAutoBarren] = useState(true);
  const [unsupMethod, setUnsupMethod] = useState("kmeans");
  const [unsupBusy, setUnsupBusy] = useState(false);
  const unsup = project.prospectivity_unsup;
  const hasFeatures = !!project.layers && Object.keys(project.layers).length > 0;
  const fieldData = project.field_data || [];

  const train = async () => {
    const positives = pts.trim().split(/\n+/)
      .map((l) => l.split(/[,\s]+/).map(Number).filter((x) => !isNaN(x)))
      .filter((p) => p.length >= 2)
      .map((p) => [p[1] ?? p[0], p[0]]); // input "lat, lon" -> [lon, lat]
    if (positives.length < 3) {
      toast.error("Enter at least 3 known occurrence points (lat, lon)");
      return;
    }
    // auto-select barren points from low-grade field samples
    let negatives = null;
    const graded = fieldData.filter((s) => s.value != null);
    if (autoBarren && graded.length >= 3) {
      const sorted = [...graded].sort((a, b) => a.value - b.value);
      const cut = sorted[Math.max(0, Math.floor(sorted.length * 0.3) - 1)].value;
      negatives = sorted.filter((s) => s.value <= cut).map((s) => [s.lon, s.lat]);
    }
    setBusy(true);
    try {
      await api.post(`/projects/${id}/ml/train`, { positives, negatives });
      toast.dismiss();
      toast.success("Model trained - probability map & ranked targets updated");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const maxImp = Math.max(1e-6, ...((ml?.importances || []).map((i) => i.importance)));

  const runUnsup = async () => {
    setUnsupBusy(true);
    try {
      await api.post(`/projects/${id}/ml/unsupervised`, { method: unsupMethod, n_clusters: 5 });
      toast.success("Unsupervised targets generated - no known occurrences required");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setUnsupBusy(false); }
  };

  return (
    <div className="space-y-4">
      {!hasFeatures && <p className="text-sm text-muted-foreground">Run pre-processing first - the model learns from the computed spectral index layers.</p>}
      <div>
        <p className="overline mb-1">Known Occurrence Points (training)</p>
        <p className="text-xs text-muted-foreground mb-2">One point per line as <code className="font-mono">lat, lon</code>. Negatives are auto-sampled from low-anomaly ground.</p>
        <Textarea rows={5} value={pts} onChange={(e) => setPts(e.target.value)} className="font-mono text-xs"
          placeholder={"-2.865, 32.279\n-2.850, 32.276\n-2.862, 32.269"} data-testid="ml-points" />
        {(project.field_data || []).length > 0 && (
          <button className="text-xs text-primary hover:underline mt-1" data-testid="ml-fill-geo"
            onClick={() => setPts((project.field_data || []).map((s) => `${s.lat}, ${s.lon}`).join("\n"))}>
            Use {(project.field_data || []).length} uploaded geological points as positives
          </button>
        )}
      </div>
      <Button className="w-full" onClick={train} disabled={busy || !hasFeatures} data-testid="ml-train">
        {busy ? <><CircleNotch size={16} className="mr-1 animate-spin" />Training Random Forest…</> : <><Brain size={16} className="mr-1" weight="fill" />Train Supervised Model</>}
      </Button>
      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="ml-auto-barren">
        <input type="checkbox" checked={autoBarren} onChange={(e) => setAutoBarren(e.target.checked)} className="accent-primary" />
        Auto-select barren points from low-grade field samples
        {fieldData.filter((s) => s.value != null).length > 0 && <span className="text-primary">({fieldData.filter((s) => s.value != null).length} graded)</span>}
      </label>

      <div className="border-t border-border pt-3">
        <p className="overline mb-1">Unsupervised (no known occurrences)</p>
        <p className="text-xs text-muted-foreground mb-2">Surface targets directly from the spectral data - no training points needed.</p>
        <div className="flex gap-2">
          <Select value={unsupMethod} onValueChange={setUnsupMethod}>
            <SelectTrigger className="h-9 text-xs flex-1" data-testid="unsup-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="kmeans">K-Means clustering</SelectItem>
              <SelectItem value="isolation_forest">Isolation Forest (anomaly)</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={runUnsup} disabled={unsupBusy || !hasFeatures} data-testid="unsup-run">
            {unsupBusy ? <CircleNotch size={16} className="animate-spin" /> : "Run"}
          </Button>
        </div>
        {unsup && (
          <div className={`border rounded-md p-3 mt-2 ${activeOverlayKey === "prospectivity_unsup" ? "border-primary bg-primary/5" : "border-border"}`}>
            <div className="flex gap-3">
              <img src={unsup.png} alt="unsup" className="w-14 h-14 rounded object-cover border border-border" style={{ imageRendering: "pixelated" }} />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">{unsup.method}</div>
                {unsup.info?.cluster_anomaly_means && <div className="font-mono text-[10px] text-muted-foreground mt-1">cluster anomaly means: {unsup.info.cluster_anomaly_means.join(", ")}</div>}
              </div>
            </div>
            <Button size="sm" className="w-full mt-2 h-8" variant={activeOverlayKey === "prospectivity_unsup" ? "secondary" : "default"}
              onClick={() => setOverlay(activeOverlayKey === "prospectivity_unsup" ? null : { key: "prospectivity_unsup", png: unsup.png, bbox })} data-testid="unsup-show-map">
              {activeOverlayKey === "prospectivity_unsup" ? "Hide from map" : "Show anomaly map"}
            </Button>
          </div>
        )}
      </div>

      {ml && (
        <div className="space-y-3">
          <div className={`border rounded-md p-3 ${activeOverlayKey === "prospectivity_ml" ? "border-primary bg-primary/5" : "border-priority-high/40"}`}>
            <div className="flex gap-3">
              <img src={ml.png} alt="ml" className="w-14 h-14 rounded object-cover border border-border" style={{ imageRendering: "pixelated" }} />
              <div className="flex-1">
                <div className="flex items-center gap-2"><span className="font-display font-semibold text-sm">{ml.method}</span><Badge className="bg-priority-high text-white text-[9px]">ML</Badge></div>
                <div className="font-mono text-[10px] mt-1">acc {ml.metrics?.train_accuracy} · OOB {ml.metrics?.oob_score} · CV {ml.metrics?.cv_accuracy}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{ml.metrics?.positives} positives · {ml.metrics?.negatives} negatives</div>
                {ml.metrics?.negatives_source && <div className="font-mono text-[10px] text-muted-foreground">barren: {ml.metrics.negatives_source}</div>}
              </div>
            </div>
            <Button size="sm" className="w-full mt-2 h-8" variant={activeOverlayKey === "prospectivity_ml" ? "secondary" : "default"}
              onClick={() => setOverlay(activeOverlayKey === "prospectivity_ml" ? null : { key: "prospectivity_ml", png: ml.png, bbox })} data-testid="ml-show-map">
              {activeOverlayKey === "prospectivity_ml" ? "Hide from map" : "Show probability map"}
            </Button>
          </div>
          <div>
            <p className="overline mb-2">Feature Importance (explainability)</p>
            {(ml.importances || []).map((f) => (
              <div key={f.feature} className="mb-1.5" data-testid={`imp-${f.feature}`}>
                <div className="flex justify-between text-[11px]"><span>{f.feature.replace(/_/g, " ")}</span><span className="font-mono">{(f.importance * 100).toFixed(1)}%</span></div>
                <div className="h-1.5 bg-muted rounded overflow-hidden"><div className="h-full bg-primary" style={{ width: `${(f.importance / maxImp) * 100}%` }} /></div>
              </div>
            ))}
          </div>
          {ml.shap_summary && (
            <div data-testid="shap-summary">
              <p className="overline mb-2">SHAP feature attribution</p>
              {ml.shap_summary.map((f) => (
                <div key={f.feature} className="flex justify-between text-[11px] mb-0.5" data-testid={`shap-${f.feature}`}>
                  <span>{f.feature.replace(/_/g, " ")}</span><span className="font-mono">{f.mean_abs_shap}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
